// M5.5: reusable matrix UI for the per-team status_transition_xp
// rows. Used by:
//   1. /admin/status-transitions (top-level — team-picker shell
//      wraps this; kept for backward compat with old links and
//      bookmarks)
//   2. /admin/ingestors/:id/transitions (Jira-ingestor-scoped —
//      no team picker; the team is implicit from the ingestor)
//
// Why a matrix and not the rule DSL: the data is table-shaped
// (from × to grid), the matching logic is different (engine looks
// at payload.transition, not rule conditions), and the UX is
// cleaner without a DSL editor. See migration 0023 + M5.5 ADR.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';

interface Competency {
  id: string;
  name: string;
  slug: string;
  dimension: 'technical' | 'behavioral';
}
export interface StatusTransitionXp {
  id: string;
  team_id: string;
  from_status: string;
  to_status: string;
  competency_id: string;
  xp: number;
  // M5.8: parallel coin credit per matrix row. Defaults
  // to 0 for legacy rows; admins opt in by setting a value
  // in the editor (or via the api's PATCH endpoint).
  coins: number;
  enabled: boolean;
  // v0.6.2: optional human-readable description shown
  // in the live feed, player dashboard, and coin history
  // when the matrix row fires. Same pattern as
  // scoring_rule.description (0030). The admin UI caps
  // the input at 500 characters; the api accepts up to
  // 500. Empty / null = no description; the UI falls back
  // to "Issue transition" in that case.
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface NewRowForm {
  from_status: string;
  to_status: string;
  competency_id: string;
  xp: number;
  // M5.8: parallel coin credit for new rows. The form
  // defaults to 0 (no coins) so the matrix behaves like
  // before unless the admin explicitly opts in.
  coins: number;
  enabled: boolean;
  // v0.6.2: optional description. Empty string = no
  // description (matches the api's empty-string-to-null
  // normalisation on POST).
  description: string;
}

const emptyForm = (): NewRowForm => ({
  from_status: '',
  to_status: '',
  competency_id: '',
  xp: 2,
  coins: 0,
  enabled: true,
  description: '',
});

// v0.6.2: 500-character cap on the matrix description
// (matches scoring_rule.description). The api also
// caps at 500 (zod schema). The browser counter is the
// operator-readable signal; the api cap is the source of
// truth for what's accepted.
const DESCRIPTION_MAX = 500;

interface Props {
  /** The team whose status_transition_xp rows we manage. */
  teamId: string;
  /**
   * If provided, the matrix fetches the Jira project's status
   * names from this ingestor and exposes them in a <datalist>
   * for the from/to pickers. If omitted, the from/to fields are
   * free-text and a "no Jira ingestor" hint is shown.
   */
  jiraInstanceId?: string;
  /**
   * Set to FALSE to hide the "no Jira ingestor" free-text hint
   * (e.g. when the page is reached from a Jira ingestor and
   * there's no scenario where the hint applies). Default true.
   */
  showJiraIngestorHint?: boolean;
  /** Optional CTA link for the "no Jira ingestor" hint. */
  ingestorSetupHref?: string;
}

export function TransitionsMatrix({
  teamId,
  jiraInstanceId,
  showJiraIngestorHint = true,
  ingestorSetupHref = '/admin/ingestors',
}: Props) {
  const qc = useQueryClient();

  const competencies = useQuery({
    queryKey: ['competencies'],
    queryFn: () => api<{ competencies: Competency[] }>('/api/competencies'),
  });

  const transitions = useQuery<{ transitions: StatusTransitionXp[] }>({
    queryKey: ['status-transitions', teamId],
    queryFn: () => api(`/api/admin/status-transitions?team_id=${teamId}`),
    enabled: !!teamId,
  });

  const jiraStatuses = useQuery<{ statuses: string[] }>({
    queryKey: ['jira-statuses', jiraInstanceId],
    queryFn: () => api(`/api/admin/status-transitions/jira-statuses?instance_id=${jiraInstanceId!}`),
    enabled: !!jiraInstanceId,
    staleTime: 5 * 60_000,
  });

  const [newRow, setNewRow] = useState<NewRowForm>(emptyForm);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api<{ transition: StatusTransitionXp }>(`/api/admin/status-transitions`, {
      method: 'POST',
      body: { ...newRow, team_id: teamId },
    }),
    onSuccess: () => {
      setNewRow(emptyForm());
      setErr(null);
      qc.invalidateQueries({ queryKey: ['status-transitions', teamId] });
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? `${e.status}: ${e.message}` : String(e);
      setErr(msg);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<NewRowForm> }) =>
      api<{ transition: StatusTransitionXp }>(`/api/admin/status-transitions/${id}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['status-transitions', teamId] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/admin/status-transitions/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['status-transitions', teamId] }),
  });

  return (
    <div className="space-y-6">
      {((jiraStatuses.data?.statuses ?? []).length) > 0 && (
        <div className="card p-3 text-xs text-ink-muted">
          <strong>Jira statuses in this project:</strong>{' '}
          {(jiraStatuses.data?.statuses ?? []).map((s) => (
            <code key={s} className="mr-1 rounded bg-bg-elevated px-1.5 py-0.5 text-ink">{s}</code>
          ))}
        </div>
      )}

      <section className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-elevated text-left text-xs uppercase text-ink-muted">
            <tr>
              <th className="px-3 py-2">From</th>
              <th className="px-3 py-2">To</th>
              <th className="px-3 py-2">XP</th>
              <th className="px-3 py-2">🪙 Coins</th>
              <th className="px-3 py-2">Competency</th>
              {/* v0.6.2: row description column. Matches the
                  scoring rule description pattern (0030);
                  otherwise the matrix awards fall back to
                  "Issue transition" on the live feed and
                  player dashboard even when the admin has
                  set a description. Compact view: ellipsis
                  after a few chars, full text in the title
                  (hover) and the inline editor. */}
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2">Enabled</th>
              <th className="px-3 py-2 w-px" />
            </tr>
          </thead>
          <tbody>
            {transitions.isLoading && (
              <tr><td colSpan={8} className="px-3 py-4 text-ink-muted">Loading…</td></tr>
            )}
            {transitions.data?.transitions?.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-4 text-ink-muted">
                No transitions yet. Add the first row below.
              </td></tr>
            )}
            {transitions.data?.transitions?.map((t) => (
              <tr key={t.id} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs">
                  {t.from_status === '' ? <em className="text-ink-muted">(any)</em> : t.from_status}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{t.to_status}</td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    className="w-20 rounded border border-border bg-bg px-2 py-0.5 text-sm"
                    defaultValue={t.xp}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v !== t.xp) update.mutate({ id: t.id, patch: { xp: v } });
                    }}
                  />
                </td>
                {/* M5.8: parallel coin credit per matrix row.
                    Same shape as the XP field. Edit on blur
                    (the table is dense; an inline change on
                    every keystroke would re-fetch constantly). */}
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min={0}
                    max={10000}
                    className="w-20 rounded border border-border bg-bg px-2 py-0.5 text-sm"
                    defaultValue={t.coins}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v !== t.coins) update.mutate({ id: t.id, patch: { coins: v } });
                    }}
                    title="Coins credited when this transition fires (M5.8)"
                  />
                </td>
                <td className="px-3 py-2">
                  <select
                    className="rounded border border-border bg-bg px-2 py-0.5 text-sm"
                    defaultValue={t.competency_id}
                    onChange={(e) => update.mutate({ id: t.id, patch: { competency_id: e.target.value } })}
                  >
                    {competencies.data?.competencies?.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </td>
                {/* v0.6.2: row description. Inline text input
                    that saves on blur. Empty string clears
                    the description (the api normalises to
                    NULL). The placeholder reminds the admin
                    this is the text the player sees on the
                    live feed + their dashboard ('Approved
                    for release' / 'Triage complete' / etc.).
                    The text-[#] gives a hint about the
                    ceiling and the live reminder when the
                    admin types too much. */}
                <td className="px-3 py-2">
                  <input
                    type="text"
                    maxLength={DESCRIPTION_MAX}
                    className="w-48 rounded border border-border bg-bg px-2 py-0.5 text-sm"
                    defaultValue={t.description ?? ''}
                    placeholder="e.g. Approved for release"
                    title={t.description ?? ''}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      const existing = (t.description ?? '').trim();
                      // Empty string = clear. Same string = no-op.
                      // The form sends the raw text (not null);
                      // the api normalises empty -> null.
                      if (v === existing) return;
                      update.mutate({ id: t.id, patch: { description: v === '' ? '' : v } });
                    }}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    defaultChecked={t.enabled}
                    onChange={(e) => update.mutate({ id: t.id, patch: { enabled: e.target.checked } })}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    className="text-xs text-ink-muted hover:text-red-400"
                    onClick={() => {
                      if (window.confirm(`Delete transition ${t.from_status || '(any)'} → ${t.to_status}?`)) {
                        remove.mutate(t.id);
                      }
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="card p-4 space-y-3">
        <h2 className="text-lg font-semibold">Add a transition</h2>
        <div className="grid gap-3 sm:grid-cols-6">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-ink-muted">From (empty = any)</label>
            <input
              list="jira-statuses-list"
              className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm font-mono"
              value={newRow.from_status}
              onChange={(e) => setNewRow((f) => ({ ...f, from_status: e.target.value }))}
              placeholder="(any)"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-ink-muted">To</label>
            <input
              list="jira-statuses-list"
              className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm font-mono"
              value={newRow.to_status}
              onChange={(e) => setNewRow((f) => ({ ...f, to_status: e.target.value }))}
              placeholder="In Review"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-ink-muted">XP</label>
            <input
              type="number"
              min={1}
              max={1000}
              className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
              value={newRow.xp}
              onChange={(e) => setNewRow((f) => ({ ...f, xp: Number(e.target.value) }))}
            />
          </div>
          {/* M5.8: parallel coin credit. Same int constraint
              as the api's zod schema (0..10000). 0 means
              "no coins" (default). */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-ink-muted">🪙 Coins</label>
            <input
              type="number"
              min={0}
              max={10000}
              className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
              value={newRow.coins}
              onChange={(e) => setNewRow((f) => ({ ...f, coins: Number(e.target.value) }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-ink-muted">Competency</label>
            <select
              className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
              value={newRow.competency_id}
              onChange={(e) => setNewRow((f) => ({ ...f, competency_id: e.target.value }))}
            >
              <option value="">— select —</option>
              {competencies.data?.competencies?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="button"
              className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-ink disabled:opacity-50"
              disabled={!newRow.to_status || !newRow.competency_id || newRow.xp <= 0 || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
        {/* v0.6.2: description field on the "add" form. Wider
            than the other columns (the player-facing text is
            usually 3-6 words like "Approved for release" or
            "Triage complete"). The character counter is the
            browser-native maxLength guard plus a small live
            counter so the admin sees the cap before they hit
            it. */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-ink-muted">
            Description (optional — shown on the live feed and player dashboard when this transition fires)
          </label>
          <textarea
            maxLength={DESCRIPTION_MAX}
            rows={2}
            className="rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
            value={newRow.description}
            onChange={(e) => setNewRow((f) => ({ ...f, description: e.target.value }))}
            placeholder="e.g. Approved for release, Triage complete, Kicked back to dev"
          />
          <p className="text-xs text-ink-muted text-right">
            {newRow.description.length} / {DESCRIPTION_MAX}
          </p>
        </div>
        {err && <p className="text-sm text-red-400">{err}</p>}
        {jiraStatuses.data && (
          <datalist id="jira-statuses-list">
            {(jiraStatuses.data?.statuses ?? []).map((s) => <option key={s} value={s} />)}
          </datalist>
        )}
        {!jiraStatuses.data && jiraInstanceId && (
          <p className="text-xs text-ink-muted">Loading Jira statuses…</p>
        )}
        {!jiraInstanceId && showJiraIngestorHint && (
          <p className="text-xs text-ink-muted">
            No active Jira ingestor for this team — the from/to fields are free-text. Configure one under{' '}
            <Link className="underline" to={ingestorSetupHref}>Ingestors</Link>.
          </p>
        )}
      </section>

      <p className="text-xs text-ink-muted">
        Transitions are scored on the user who made the change (the Jira <code>changelog.author</code>).
        Multiple matrix rows can match a single transition (e.g. a wildcard "(any) → Done" plus a specific
        "Code Review → Done"); the engine credits both. The dedup window is 60s per (user, issue, to-status),
        so two different transitions on the same issue in the same minute (e.g. A→B and B→C) still credit
        both. The active award is visible in each player's XP history.
        {' '}
        <strong>Description:</strong> each row's optional "description" is what the player sees
        on the live feed, their player dashboard, and their coin history when this transition
        fires. E.g. <em>Approved for release</em>, <em>Triage complete</em>, <em>Reopened</em>.
        Without a description, the row falls back to the generic <em>Issue transition</em> label.
      </p>
    </div>
  );
}
